import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useState } from 'react';
import { requestPasswordRecovery } from '../services/LoginService';
import { routes } from '../routes/routePaths';

import Card from '../components/card/Card';
import H from '../components/texts/h/H';
import Input from '../components/input/Input';
import P from '../components/texts/p/P';
import Button from '../components/button/Button';
import Spinner from '../components/Spinner/spinner';
import Link from '../components/link/Link';
import { useClientLogo } from '../hooks/useClientLogo';

type PageState = 'form' | 'success' | 'error';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { logoUrl } = useClientLogo();
  const [pageState, setPageState] = useState<PageState>('form');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const validate = (values: { email: string }) => {
    const errors: any = {};
    if (!values.email) {
      errors.email = 'El campo email es obligatorio';
    } else if (
      !/^[A-Za-z0-9+_.-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(values.email)
    ) {
      errors.email = 'Ingresá un email válido';
    }
    return errors;
  };

  const formik = useFormik({
    initialValues: { email: '' },
    validate,
    onSubmit: async (values) => {
      setIsLoading(true);
      setErrorMsg('');

      const response = await requestPasswordRecovery(values.email);

      setIsLoading(false);

      if (response.success) {
        setPageState('success');
      } else if (response.code === 'INVALID_EMAIL') {
        setErrorMsg(response.message || 'El email ingresado no es válido.');
      } else {
        setPageState('error');
      }
    },
  });

  const renderForm = () => (
    <>
      <H
        withMargin={false}
        variant='primary'
        type='3'
        weight='bolder'
        modifier='strong'
        align='center'
      >
        Recuperar contraseña
      </H>
      <Card margin={{ top: '2' }}>
        <P type='1' align='center' variant='neutral'>
          Ingresá tu email y te enviaremos un enlace para restablecer tu
          contraseña.
        </P>
      </Card>
      <form onSubmit={formik.handleSubmit}>
        <Card margin={{ top: '3' }}>
          <Card margin={{ bottom: '3' }}>
            <Input
              id='forgot-password-input-email'
              inputStatus={
                formik.touched.email && formik.errors.email ? 'error' : 'normal'
              }
              label='Email'
              placeholder='Ingresá tu email'
              size='md'
              type='text'
              onChange={(e) => formik.setFieldValue('email', e.target.value)}
            />
            {formik.touched.email && formik.errors.email ? (
              <P variant='danger' withMargin={false} type='2'>
                {formik.errors.email}
              </P>
            ) : null}
          </Card>

          {errorMsg && (
            <P variant='danger' type='1' weight='bold' align='center'>
              {errorMsg}
            </P>
          )}

          <Card
            display='flex'
            justifyContent='center'
            margin={{ top: '4', bottom: '3' }}
          >
            <Button
              type='submit'
              id='forgot-password-button-send'
              size='xl'
              fullWidth
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner size='sm' variant='primary' />
              ) : (
                'Enviar enlace'
              )}
            </Button>
          </Card>
          <Card display='flex' justifyContent='center'>
            <Link
              size='sm'
              variant='primary'
              target='_self'
              onClick={() => navigate(routes.login)}
            >
              Volver al inicio de sesión
            </Link>
          </Card>
        </Card>
      </form>
    </>
  );

  const renderSuccess = () => (
    <>
      <H
        withMargin={false}
        variant='primary'
        type='3'
        weight='bolder'
        modifier='strong'
        align='center'
      >
        Revisá tu correo
      </H>
      <Card margin={{ top: '3' }}>
        <P type='1' align='center' variant='neutral'>
          Si el email ingresado está registrado, recibirás un enlace para
          restablecer tu contraseña. Revisá tu bandeja de entrada y la carpeta
          de spam.
        </P>
      </Card>
      <Card
        display='flex'
        justifyContent='center'
        margin={{ top: '6', bottom: '3' }}
      >
        <Button
          type='button'
          id='forgot-password-button-back'
          size='xl'
          fullWidth
          onClick={() => navigate(routes.login)}
        >
          Volver al inicio de sesión
        </Button>
      </Card>
    </>
  );

  const renderError = () => (
    <>
      <H
        withMargin={false}
        variant='danger'
        type='3'
        weight='bolder'
        modifier='strong'
        align='center'
      >
        No se pudo enviar el correo
      </H>
      <Card margin={{ top: '3' }}>
        <P type='1' align='center' variant='neutral'>
          Ocurrió un error al intentar enviar el enlace de recuperación. Por
          favor, intentá nuevamente más tarde.
        </P>
      </Card>
      <Card
        display='flex'
        justifyContent='center'
        margin={{ top: '6', bottom: '3' }}
      >
        <Button
          type='button'
          id='forgot-password-button-retry'
          size='xl'
          fullWidth
          onClick={() => {
            setPageState('form');
            setErrorMsg('');
            formik.resetForm();
          }}
        >
          Intentar nuevamente
        </Button>
      </Card>
      <Card display='flex' justifyContent='center'>
        <Link
          size='sm'
          variant='primary'
          target='_self'
          onClick={() => navigate(routes.login)}
        >
          Volver al inicio de sesión
        </Link>
      </Card>
    </>
  );

  return (
    <Card
      variant='primary'
      borderRadius={false}
      modifier='regular'
      display='flex'
      justifyContent='center'
      height='100vh'
      width='100%'
      alignItems='center'
    >
      <Card
        boxShadow='1'
        spacing={{ top: '6', right: '4', bottom: '4', left: '4' }}
        width='360px'
        variant='neutral'
        height='auto'
        margin='auto'
        modifier='inverted'
      >
        <Card
          display='flex'
          justifyContent='center'
          margin={{ top: '6', bottom: '3' }}
        >
          <img
            src={logoUrl || '/assets/logos/logo.svg'}
            width='64px'
            alt='Logo'
          />
        </Card>

        {pageState === 'form' && renderForm()}
        {pageState === 'success' && renderSuccess()}
        {pageState === 'error' && renderError()}
      </Card>
    </Card>
  );
};

export default ForgotPassword;
